<template>
    <div class="assigned-editor">
        <div class="ae-head">
            <toolbar-stepper :step="carousel" :items="stepperTabs"/>
        </div>

        <div class="ae-body">
            <v-carousel :show-arrows="false" hide-delimiters touchless v-model="carousel" height="auto">
                <!-- BASIC INFO -->
                <v-carousel-item>
                    <routine-name
                        no-title
                        :defaults="routineNameInfo"
                        @change="onNameChange"
                        @img="onImgChange"
                        @clear="onClearImg"
                        ref="routineName"
                    />
                </v-carousel-item>
                <v-carousel-item>
                    <div class="ae-step-title">
                        {{ $t('routines.add_blocks_and_exercises') }}
                    </div>
                    <routine-blocks
                        ref="blocksManager"
                        @close:exercises-dialog="onExercisesDialogClose"
                        :default-blocks="blocks"
                        @change="blocks = $event"/>
                </v-carousel-item>
            </v-carousel>
        </div>

        <div class="ae-actions">
            <v-btn depressed height="42" class="normal-btn ae-btn ae-btn-cancel" @click="goBack">
                {{ cancelLabel }}
            </v-btn>
            <v-btn depressed height="42" class="normal-btn ae-btn ae-btn-next black--text" @click="continueStepper"
                :loading="loading">
                {{ nextLabel }}
            </v-btn>
        </div>
    </div>
</template>

<script>

import RoutineName from "@/components/coach/routines/RoutineName";
import RoutineBlocks from "@/components/coach/routines/RoutineBlocks";


import _map from 'lodash/map'
import _flatten from 'lodash/flatten'
import _uniq from 'lodash/uniq'
import _compact from 'lodash/compact'

import ToolbarStepper from "@/components/elements/ToolbarStepper";
import { getPayloadExercise } from "../../../utils/Exercises";

export default {
    name: "AssignedRoutineEditor",
    components: {
        ToolbarStepper,
        RoutineName,
        RoutineBlocks,
    },
    props: {
        defaultRoutine: {
            type: Object
        },
    },
    data(vm) {
        return {
            routineID: null,
            loading: false,
            carousel: 0,
            name: '',
            uploadedImg: null,
            imgCleared: false,
            imgUrl: '',
            objective: '',
            blocks: [{exercises: []}],
            currentError: '',
            assignValid: false,

            stepperTabs: [
                vm.$t('steppers.details'),
                vm.$t('steppers.blocks_and_exercises'),
            ]
        }
    },
    computed: {
        routineNameInfo(vm = this) {
            return {
                name: vm.name,
                img: vm.imgUrl,
                objective: vm.objective
            }
        },
        cancelLabel(vm = this) {
            if (vm.carousel === 1) return vm.$t('common.back_to_details')
            return vm.$t('common.cancel')
        },
        nextLabel(vm = this) {
            if (vm.carousel === 1) return vm.$t('common.save')
            return vm.$t('common.next')
        },
        isValid(vm = this) {
            vm.currentError = ''
            if (vm.carousel === 0) {
                if (!vm.name) {
                    vm.currentError = vm.$t('routines.routine_name_required')
                    return false
                }
                if (!vm.objective) {
                    vm.currentError = vm.$t('routines.routine_objective_required')
                    return false
                }
            }

            if (vm.carousel === 1) {
                if (vm.blocks.length === 0) {
                    vm.currentError = vm.$t('routines.routine_blocks_required')
                    return false
                }
                let blockValid = true

                vm.blocks.forEach(block => {
                    if (block.exercises.length === 0) {
                        vm.currentError = vm.$t('routines.routine_exercises_required')
                        return blockValid = false
                    }

                    if (block.exercises[0].type === 'rest') {
                        vm.currentError = 'No se puede agregar un descanso al comienzo'
                        return blockValid = false
                    }

                    let qtyExerciseWithConfig = 0
                    let qtyRestContinuos = 0
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest' && exercise.config && exercise.config.sets?.length > 0) qtyExerciseWithConfig++
                        if (exercise.type === 'rest') {
                            qtyRestContinuos++
                            if (qtyRestContinuos === 2) {
                                vm.currentError = 'No se puede tener 2 descansos seguidos'
                                return blockValid = false
                            }
                        } else {
                            qtyRestContinuos = 0
                        }
                    })

                    if (qtyExerciseWithConfig !== block.exercises.filter(ex => ex.type !== 'rest').length) {
                        vm.currentError = vm.$t('routines.routine_sets_required')
                        return blockValid = false
                    }
                })

                return blockValid
            }

            vm.currentError = ''
            return true
        },
        routinePayload(vm = this) {
            const exercises = _flatten(_map(vm.blocks, 'exercises'))
            const equipments = _uniq(_compact(_flatten(_map(exercises, 'equipments'))))

            return {
                name: vm.name || '',
                img: vm.uploadedImg?.url || (vm.imgCleared ? '' : vm.defaultRoutine?.img) || '',
                imgSavePath: vm.uploadedImg?.savePath || (vm.imgCleared ? '' : vm.defaultRoutine?.imgSavePath) || '',
                description: {
                    equipments: equipments,
                    blocks: vm.blocks.length,
                    objective: vm.objective || '',
                    objectiveKey: vm.routineObjectiveKey(vm.objective) || ''
                },
                blocks: vm.blocks && vm.blocks.map((block, i) => {
                    return {
                        id: Number(i) + 1,
                        completed: block.completed || false,
                        exercises: block?.exercises.map(exercise => getPayloadExercise(exercise)) || []
                    }
                }) || []
            }
        }
    },
    mounted() {
        const vm = this
        vm.setDefaults()
    },
    methods: {
        onNameChange(data) {
            let vm = this
            vm.name = data.name || ''
            vm.imgName = data.imgName || ''
            vm.imgUrl = data.imgUrl || ''
            vm.objective = data.objective || ''
        },
        onImgChange(img) {
            const vm = this
            vm.uploadedImg = img || null
            if (img) vm.imgCleared = false
        },
        onClearImg() {
            const vm = this
            vm.uploadedImg = null
            vm.imgCleared = true
        },
        setDefaults() {
            const vm = this
            if (!vm.defaultRoutine) return;
            vm.name = vm.defaultRoutine.name || ''
            vm.imgUrl = vm.defaultRoutine.img || ''
            vm.objective = vm.defaultRoutine?.description?.objective || ''
            vm.blocks = vm.defaultRoutine.blocks || []
            // console.log('vm.blocks',vm.blocks)
        },
        reset() {
            const vm = this
            vm.carousel = 0
            vm.name = ''
            vm.uploadedImg = null
            vm.imgCleared = false
            vm.objective = ''
            vm.blocks = []
        },
        onExercisesDialogClose() {
            const vm = this
            vm.$nextTick(function () {
                vm.setOnBackPress()
            })
        },
        setOnBackPress() {
            const vm = this
            vm.onBackPress(vm.goBack)
        },
        goBack() {
            const vm = this
            if (vm.carousel === 0) return vm.$router.back()
            vm.carousel--
        },
        continueStepper() {
            const vm = this
            if (!vm.isValid) return vm.toast(vm.currentError, 'error')
            if (vm.carousel === 1) return vm.save()
            if (vm.carousel === 2) return vm.$refs.assignRoutine.checkAssignedRoutines()
            return vm.carousel++
        },
        async save() {
            const vm = this

            if (!vm.isValid) return vm.toast(vm.currentError, 'error')

            await vm.withLoading(async () => {
                await vm.$refs.routineName.upload()

                const saved = await vm.saveAssignedRoutine(vm.defaultRoutine.id, vm.routinePayload)
                if (saved) vm.toast(vm.$t('routines.assigned_routine_edited'))
                vm.$router.back()
            })
        },
    },
    watch: {
        carousel(val) {
            const vm = this
            window.scrollTo(0, 0)
            val > 0 ? vm.setOnBackPress() : vm.clearOnBack()
        },
    },
}
</script>

<style scoped>

.assigned-editor {
    max-width: 720px;
    margin: 0 auto;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.ae-head {
    padding-bottom: 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.ae-body {
    padding: 20px 0;
}

.ae-step-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-align: center;
    margin-bottom: 20px;
}

.ae-actions {
    display: flex;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid var(--v-outline-base);
}

.ae-btn {
    flex: 1 1 0;
}

.ae-btn-cancel {
    background: var(--v-surface2-base) !important;
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base) !important;
}

.ae-btn-next {
    background: var(--brand-gradient) !important;
}

</style>